import React, { useState, useMemo } from "react";
import {
  Card,
  Button,
  Table,
  InputGroup,
  Form,
  Badge,
  Spinner,
} from "react-bootstrap";
import {
  FiSearch,
  FiPlus,
  FiCalendar,
  FiClock,
  FiBook,
  
} from "react-icons/fi";
import { FaSort } from "react-icons/fa";
import AddSubjectToSeries from "./addSubjectToSeries";
import MasterActions from "../../../../views/dashboard/widget/MasterActions.js";
import {
  getExaminationSeriesSubjectList,
  deleteExaminationSeriesSubject,
  updateExaminationSeriesSubject,
} from "../../../../services/Users-services.js";
import { getMethods } from "../../../../services/utilityFunctions.js";

// Memoized SeriesTab component with table for schedules - styled like MasterTable
const SeriesTab = React.memo(
  ({ details, navigate, fetchDetails, dispatch }) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [searchLoading, setSearchLoading] = useState(false);

    // Extract schedules with null check
    const schedules = details?._SCHEDULES_ || [];

    // Get methods for permissions (similar to MasterTable)
    const methods = useMemo(() => {
      return getMethods("Examination", "Series Subject");
    }, []);

    const tableHeaders = useMemo(() => {
      if (schedules.length === 0) return [];

      const firstSchedule = schedules[0];
      // Filter out fields that start with underscore (internal IDs)
      return Object.keys(firstSchedule).filter((key) => !key.startsWith("_"));
    }, [schedules]);

    // Filter schedules based on search term
    const filteredSchedules = useMemo(() => {
      if (!searchTerm.trim()) return schedules;

      return schedules.filter((schedule) =>
        Object.values(schedule).some(
          (value) =>
            value &&
            value.toString().toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }, [schedules, searchTerm]);

    // Handle add subject series button click
    const handleAddSubjectSeries = () => {
      dispatch({
        type: "modal/openModal",
        payload: {
          isOpen: true,
          isEditing: false,
          title: "Add Subject Series",
          body: (
            <AddSubjectToSeries
              navigate={navigate}
              refresh={() => {
                fetchDetails({ id: details._ID, isLoading: false });
              }}
            />
          ),
          data: details,
        },
      });
    };

    // Handle search with loading simulation
    const handleSearch = (e) => {
      const value = e.target.value;
      setSearchTerm(value);

      if (value.trim()) {
        setSearchLoading(true);
        // Simulate search delay like in MasterTable
        setTimeout(() => {
          setSearchLoading(false);
        }, 300);
      } else {
        setSearchLoading(false);
      }
    };

    // Render table cell content with proper formatting
    const renderCellContent = (key, value) => {
      if (!value) return "-";

      // Format specific fields
      switch (key) {
        case "EXAM DATE":
          return (
            <div className="d-flex align-items-center">
              <FiCalendar className="me-1 text-primary" size={14} />
              {value}
            </div>
          );
        case "START TIME":
        case "END TIME":
          return (
            <div className="d-flex align-items-center">
              <FiClock className="me-1 text-info" size={14} />
              {value}
            </div>
          );
        case "SUBJECT":
          return (
            <div className="d-flex align-items-center">
              <FiBook className="me-1 text-success" size={14} />
              {value}
            </div>
          );
        case "EXAM TYPE":
          return (
            <Badge
              bg=""
              style={{
                padding: "4px 7px",
                fontSize: "0.75rem",
                fontWeight: "400",
                backgroundColor:
                  value === "Subjective"
                    ? "#e3f2fd"
                    : value === "Objective"
                    ? "#e8f5e9"
                    : "#f3e5f5",
                color:
                  value === "Subjective"
                    ? "#1976d2"
                    : value === "Objective"
                    ? "#388e3c"
                    : "#7b1fa2",
                borderRadius: "4px",
                textTransform: "uppercase",
                letterSpacing: "0.4px",
              }}
            >
              {value}
            </Badge>
          );
        case "EXAM MODE":
          return (
            <Badge
              bg=""
              style={{
                padding: "4px 7px",
                fontSize: "0.75rem",
                fontWeight: "400",
                backgroundColor: value === "Online" ? "#e1f5fe" : "#fff3e0",
                color: value === "Online" ? "#0277bd" : "#f57c00",
                borderRadius: "4px",
                textTransform: "uppercase",
                letterSpacing: "0.4px",
              }}
            >
              {value}
            </Badge>
          );
        default:
          return value;
      }
    };

    // Table Header Cell component (similar to MasterTable)
    const TableHeaderCell = ({ columnName }) => (
      <th style={{ verticalAlign: "middle" }}>
        {columnName}{" "}
        <FaSort
          style={{
            cursor: "pointer",
            transition: "transform 0.2s ease",
          }}
          className="hover-scale"
        />
      </th>
    );

    return (
      <Card className="shadow-sm m-0 p-0">
        <Card.Header className="pb-3">
          {/* Header section matching MasterTable style */}
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 gap-2 slide-in">
            <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center gap-2">
              <h4 className="mb-1 mb-sm-0">Series Schedules</h4>
              {schedules.length > 0 && (
                <Badge
                  bg="primary"
                  className="align-self-start align-self-sm-center"
                  style={{
                    fontSize: "0.75rem",
                    padding: "4px 8px",
                    borderRadius: "12px",
                    fontWeight: "500",
                  }}
                >
                  {schedules.length}
                </Badge>
              )}
            </div>

            <div className="d-flex flex-column flex-md-row align-items-end align-items-md-end w-md-auto gap-2">
              <div className="d-flex flex-wrap justify-content-end align-items-center gap-2">
                {details?.STATUS === "Draft" && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleAddSubjectSeries}
                    className="hover-scale"
                  >
                    Add Subject Series
                  </Button>
                )}
              </div>

              {/* Search input matching MasterTable style */}
              <InputGroup
                className="w-auto flex-grow-1 flex-md-grow-0"
                style={{
                  maxWidth: "400px",
                  border: "1px solid #dee2e6",
                  borderRadius: "0.375rem",
                  overflow: "hidden",
                  transition: "all 0.2s ease",
                }}
              >
                <InputGroup.Text
                  className={`bg-transparent border-0 ${
                    searchLoading ? "pe-2" : "pe-1"
                  }`}
                  style={{
                    transition: "all 0.2s ease",
                  }}
                >
                  {searchLoading ? (
                    <Spinner
                      animation="border"
                      role="status"
                      size="sm"
                      className="text-primary"
                      style={{ width: "1.2rem", height: "1.2rem" }}
                    />
                  ) : (
                    <FiSearch className="text-muted" />
                  )}
                </InputGroup.Text>

                <Form.Control
                  size="sm"
                  type="text"
                  placeholder="Search schedules..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="border-0 ps-1"
                  style={{
                    transition: "all 0.2s ease",
                    outline: "none",
                    border: "none !important",
                    boxShadow: "none",
                  }}
                  onFocus={(e) => {
                    e.target.parentElement.style.borderColor = "#86b7fe";
                    e.target.parentElement.style.boxShadow =
                      "0 0 0 0.2rem rgba(13, 110, 253, 0.25)";
                  }}
                  onBlur={(e) => {
                    e.target.parentElement.style.borderColor = "#dee2e6";
                    e.target.parentElement.style.boxShadow = "none";
                  }}
                />
              </InputGroup>
            </div>
          </div>
        </Card.Header>

        <Card.Body className="pt-0 mt-0">
          {schedules.length === 0 ? (
            // Empty state
            <div className="text-center py-5 fade-in">
              <FiCalendar size={48} className="text-muted mb-3" />
              <h6 className="text-muted mb-2">No Schedules Found</h6>
              <p className="text-muted small mb-3">
                No examination schedules have been added to this series yet.
              </p>
              {details?.STATUS === "Draft" && (
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={handleAddSubjectSeries}
                  className="d-flex align-items-center gap-2 mx-auto hover-scale"
                >
                  <FiPlus size={16} />
                  Add Your First Schedule
                </Button>
              )}
            </div>
          ) : (
            <div
              className="table-responsive border table-container fade-in"
              style={{
                minHeight: "40vh",
                maxHeight: "60vh",
                overflow: "auto",
                overflowX: "visible",
                borderRadius: "15px",
                boxShadow:
                  "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
              }}
            >
              <Table
                hover
                className="mb-0 table-fixed"
                style={{
                  position: "relative",
                  borderCollapse: "separate",
                  borderSpacing: 0,
                  overflow: "auto",
                  borderRadius: "15px",
                }}
              >
                <thead
                  style={{
                    position: "sticky",
                    top: 0,
                    background: "white",
                    zIndex: 2,
                    boxShadow: "0 2px 2px -1px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <tr
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: "400px",
                      textAlign: "center",
                      verticalAlign: "middle",
                    }}
                  >
                    {tableHeaders.map((header, index) => (
                      <TableHeaderCell key={index} columnName={header} />
                    ))}
                    <th
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: "400px",
                        textAlign: "center",
                        verticalAlign: "middle",
                      }}
                    >
                      ACTION
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSchedules.length === 0 ? (
                    <tr>
                      <td
                        colSpan={tableHeaders.length}
                        className="text-center py-4 text-muted"
                      >
                        <FiSearch size={20} className="me-2" />
                        No schedules match your search criteria
                      </td>
                    </tr>
                  ) : (
                    filteredSchedules.map((schedule, index) => (
                      <tr
                        key={schedule._ID || index}
                        className="table-row"
                        style={{
                          maxHeight: "40px",
                          animation: `fadeIn 0.3s ease-in-out ${
                            index * 0.05
                          }s both`,
                          transition: "background-color 0.2s ease",
                        }}
                      >
                        {tableHeaders.map((header, headerIndex) => (
                          <td
                            key={headerIndex}
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              maxWidth: "400px",
                              textAlign:
                                header === "SUBJECT" ? "left" : "center",
                              verticalAlign: "middle",
                              transition: "background-color 0.15s ease",
                            }}
                          >
                            {renderCellContent(header, schedule[header])}
                          </td>
                        ))}
                        <td style={{ textAlign: "center" }}>
                          <MasterActions
                            refresh={() => {
                              fetchDetails({
                                id: details._ID,
                                isLoading: false,
                              });
                            }}
                            deleteRow={deleteExaminationSeriesSubject}
                            updateStatus={null} 
                            details={details}// No status update for series subjects
                            actions={{
                              status: false,
                              edit: details?.STATUS === "Draft",
                              delete: details?.STATUS === "Draft",
                            }}
                            AddForm={
                              <AddSubjectToSeries
                                navigate={navigate}
                                refresh={() => {
                                  fetchDetails({
                                    id: details._ID,
                                    isLoading: false,
                                  });
                                }}
                              />
                            }
                            methods={methods}
                            item={schedule}
                            title="Series Subject"
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          )}

          {/* Footer with pagination-style info matching MasterTable */}
          {schedules.length > 0 && (
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-stretch mt-3 gap-2">
              <div className="d-flex justify-content-between justify-content-md-start align-items-center gap-3 flex-grow-1">
                <div className="text-nowrap">
                  Showing {filteredSchedules.length} of {schedules.length}{" "}
                  schedules
                  {searchTerm && ` matching "${searchTerm}"`}
                </div>
              </div>
            </div>
          )}
        </Card.Body>

        {/* Add CSS animations */}
        <style jsx>{`
          @keyframes fadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }

          .fade-in {
            animation: fadeIn 0.3s ease-in-out;
          }

          .slide-in {
            animation: slideIn 0.3s ease-out;
          }

          @keyframes slideIn {
            from {
              transform: translateY(-10px);
              opacity: 0;
            }
            to {
              transform: translateY(0);
              opacity: 1;
            }
          }

          .hover-scale {
            transition: transform 0.15s ease-in-out;
          }

          .hover-scale:hover {
            transform: scale(1.02);
          }

          .table-row {
            transition: background-color 0.2s ease;
          }

          .table-row:hover {
            background-color: rgba(13, 110, 253, 0.04);
          }

          .table-container {
            scroll-behavior: smooth;
          }
        `}</style>
      </Card>
    );
  }
);

export default SeriesTab;
